import type { AstroChart } from '@/api/schema'
import { PLANET_RAW_SVG_MAP } from '@/app/utils/natal'
import type { FunctionalComponent } from 'preact'
import { useEffect } from 'preact/hooks'
import styles from './index.module.scss'

const black = '#000'

const signNames = {
  Aries: 'ОВЕН',
  Taurus: 'ТЕЛЕЦ',
  Gemini: 'БЛИЗНЕЦЫ',
  Cancer: 'РАК',
  Leo: 'ЛЕВ',
  Virgo: 'ДЕВА',
  Libra: 'ВЕСЫ',
  Scorpio: 'СКОРПИОН',
  Sagittarius: 'СТРЕЛЕЦ',
  Capricorn: 'КОЗЕРОГ',
  Aquarius: 'ВОДОЛЕЙ',
  Pisces: 'РЫБЫ',
} as const

export type NatalChartPlanet = keyof typeof PLANET_RAW_SVG_MAP
export type NatalChartSign = keyof typeof signNames
export type NatalChartAspect = readonly [
  NatalChartPlanet,
  NatalChartPlanet,
  number | string,
]

const planetNames = Object.keys(PLANET_RAW_SVG_MAP) as NatalChartPlanet[]
const signNameList = Object.keys(signNames) as NatalChartSign[]
const houseNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const aspectNames: Record<number, string> = {
  0: 'conjunction',
  60: 'sextile',
  90: 'square',
  120: 'trine',
  180: 'opposition',
}

function isNatalChartPlanet(name: string): name is NatalChartPlanet {
  return name in PLANET_RAW_SVG_MAP
}

function isNatalChartSign(name: string): name is NatalChartSign {
  return name in signNames
}

function isHouseNumber(name: string) {
  const house = Number(name)

  return Number.isInteger(house) && house >= 1 && house <= 12
}

function getAspectColor(
  degree: string | null,
  positiveColor?: string,
  negativeColor?: string,
) {
  if (degree === '60' || degree === '120') return positiveColor
  if (degree === '90' || degree === '180') return negativeColor
}

function applyAspectLineStyle(
  line: SVGLineElement,
  positiveColor?: string,
  negativeColor?: string,
  strokeWidth?: number | string,
) {
  const color = getAspectColor(
    line.getAttribute('data-degree'),
    positiveColor,
    negativeColor,
  )

  if (color) {
    line.style.setProperty('stroke', color, 'important')
  }
  else {
    line.style.removeProperty('stroke')
  }

  if (strokeWidth !== undefined) {
    line.style.setProperty('stroke-width', String(strokeWidth), 'important')
  }
  else {
    line.style.removeProperty('stroke-width')
  }
}

function getHouseNumber(degree: number, cusps: number[]) {
  const normalizedDegree = ((degree % 360) + 360) % 360

  for (let index = 0; index < cusps.length; index++) {
    const start = ((cusps[index] % 360) + 360) % 360
    const end = ((cusps[(index + 1) % cusps.length] % 360) + 360) % 360
    const includesDegree = start <= end
      ? normalizedDegree >= start && normalizedDegree < end
      : normalizedDegree >= start || normalizedDegree < end

    if (includesDegree) return index + 1
  }
}

function getPlanetDegree(name: NatalChartPlanet, chart: AstroChart, cusps: number[], hasCusps: boolean) {
  if (name === 'Asc') return hasCusps ? cusps[0] : undefined
  if (name === 'Mc') return hasCusps ? cusps[9] : undefined

  return chart.planets[name]?.[0]
}

export type NatalChartProps = {
  id?: string
  chart: AstroChart
  accents?: NatalChartPlanet[] | null
  aspect?: NatalChartAspect
  houses?: number[]
  signSectionsColor?: string
  rotationDegrees?: number
  positiveAspectColor?: string
  negativeAspectColor?: string
  aspectStrokeWidth?: number | string
  accentScale?: number
}

export const NatalChart: FunctionalComponent<NatalChartProps> = props => {
  const chartId = props.id ?? 'paper'
  const signSectionsColor = props.signSectionsColor ?? black
  const hasCusps = props.chart.cusps.length > 0

  useEffect(() => {
    const { default: Chart } = require(
      '@astrodraw/astrochart',
    ) as typeof import('@astrodraw/astrochart')

    const chart = new Chart(chartId, 800, 800, {
      MARGIN: 0,
      SHIFT_IN_DEGREES: props.rotationDegrees ?? 180,
      COLOR_ARIES: signSectionsColor,
      COLOR_LEO: signSectionsColor,
      COLOR_SAGITTARIUS: signSectionsColor,
      COLOR_TAURUS: signSectionsColor,
      COLOR_VIRGO: signSectionsColor,
      COLOR_CAPRICORN: signSectionsColor,
      COLOR_GEMINI: signSectionsColor,
      COLOR_LIBRA: signSectionsColor,
      COLOR_AQUARIUS: signSectionsColor,
      COLOR_CANCER: signSectionsColor,
      COLOR_SCORPIO: signSectionsColor,
      COLOR_PISCES: signSectionsColor,
      RULER_RADIUS: 5,
      COLLISION_RADIUS: 15,

      SHOW_DIGNITIES_TEXT: false,
      ADD_CLICK_AREA: false,

      CUSPS_FONT_COLOR: '#cccccc',

      SIGNS_COLOR: '#ffffff',
      CIRCLE_STRONG: 1,
      POINTS_STROKE: 1,
      SIGNS_STROKE: 1,
      CUSPS_STROKE: 1,
      SYMBOL_AXIS_STROKE: 1,
      SYMBOL_MC: 'McAxis',
      INDOOR_CIRCLE_RADIUS_RATIO: 1.43,

      CUSTOM_SYMBOL_FN: function(name, x, y, context) {
        const xmlns = 'http://www.w3.org/2000/svg'

        if (name === 'McAxis') {
          return document.createElementNS(xmlns, 'g')
        }

        if (isHouseNumber(name)) {
          const text = document.createElementNS(xmlns, 'text')
          const cx = context.width / 2
          const cy = context.height / 2
          const angle = Math.atan2(y - cy, x - cx)
          const radius = Math.hypot(x - cx, y - cy) - 35

          text.setAttribute('x', String(cx + radius * Math.cos(angle)))
          text.setAttribute('y', String(cy + radius * Math.sin(angle)))
          text.setAttribute('id', `${chartId}-astrology-radix-house-${name}`)
          text.setAttribute(
            'class',
            hasCusps
              ? styles.houseNumber
              : `${styles.houseNumber} ${styles.hiddenHouseNumber}`,
          )
          text.textContent = name

          return text
        }

        if (isNatalChartSign(name)) {
          const g = document.createElementNS(xmlns, 'g')
          g.setAttribute('id', `${chartId}-astrology-radix-signs-${name}`)

          const cx = context.width / 2
          const cy = context.height / 2
          const margin = context.settings.MARGIN
          const radius = (context.height / 2) - margin
          const innerRatio = context.settings.INNER_CIRCLE_RADIUS_RATIO
          const signRadius = radius - (radius / innerRatio) / 2
          // Внутренний радиус чёрного кольца (граница со светлой областью внутри)
          const innerRingRadius = radius - (radius / innerRatio)

          const angleRad = Math.atan2(y - cy, x - cx)
          let angleDeg = (angleRad * 180 / Math.PI + 360) % 360

          // --- РАЗДЕЛИТЕЛЬ В НАЧАЛЕ СЕКТОРА ---
          // Центр сектора — это (x, y), т.е. угол angleRad.
          // Сектор шириной 30°, значит граница — на -15° от центра.
          // Рисуем одну границу на знак — получим 12 разделителей всего.
          const dividerAngle = angleRad - (15 * Math.PI / 180)
          const dx1 = cx + innerRingRadius * Math.cos(dividerAngle)
          const dy1 = cy + innerRingRadius * Math.sin(dividerAngle)
          const dx2 = cx + radius * Math.cos(dividerAngle)
          const dy2 = cy + radius * Math.sin(dividerAngle)

          const divider = document.createElementNS(xmlns, 'line')
          divider.setAttribute('x1', String(dx1))
          divider.setAttribute('y1', String(dy1))
          divider.setAttribute('x2', String(dx2))
          divider.setAttribute('y2', String(dy2))
          divider.setAttribute('stroke', context.settings.SIGNS_COLOR || '#ffffff')
          divider.setAttribute('stroke-width', '1')
          g.appendChild(divider)
          // --- КОНЕЦ РАЗДЕЛИТЕЛЯ ---

          const arcHalf = 13 * Math.PI / 180
          const startAngle = angleRad - arcHalf
          const endAngle = angleRad + arcHalf

          const x1 = cx + signRadius * Math.cos(startAngle)
          const y1 = cy + signRadius * Math.sin(startAngle)
          const x2 = cx + signRadius * Math.cos(endAngle)
          const y2 = cy + signRadius * Math.sin(endAngle)

          const isBottom = angleDeg > 0 && angleDeg < 180

          const pathId = `${chartId}-sign-arc-${name}`
          const path = document.createElementNS(xmlns, 'path')

          if (isBottom) {
            path.setAttribute('d', `M ${x2},${y2} A ${signRadius},${signRadius} 0 0,0 ${x1},${y1}`)
          }
          else {
            path.setAttribute('d', `M ${x1},${y1} A ${signRadius},${signRadius} 0 0,1 ${x2},${y2}`)
          }

          path.setAttribute('id', pathId)
          path.setAttribute('fill', 'none')
          path.setAttribute('stroke', 'none')

          const defs = document.createElementNS(xmlns, 'defs')
          defs.appendChild(path)
          g.appendChild(defs)

          const text = document.createElementNS(xmlns, 'text')
          text.setAttribute('font-size', '18')
          text.setAttribute('font-family', 'sans-serif')
          text.setAttribute('font-weight', '500')
          text.setAttribute('letter-spacing', '2')
          text.setAttribute('fill', context.settings.SIGNS_COLOR || '#000')

          const textPath = document.createElementNS(xmlns, 'textPath')
          textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#' + pathId)
          textPath.setAttribute('href', '#' + pathId)
          textPath.setAttribute('startOffset', '50%')
          textPath.setAttribute('text-anchor', 'middle')
          textPath.setAttribute('dominant-baseline', 'central')
          textPath.textContent = signNames[name]

          text.appendChild(textPath)
          g.appendChild(text)

          return g
        }

        // --- ПЛАНЕТЫ ---
        if (isNatalChartPlanet(name)) {
          const VB_SIZE = 30 // viewBox всех планет — 0 0 30 30
          const g = document.createElementNS(xmlns, 'g')
          const centerTransform = `translate(${x}, ${y})`
          const iconOffsetTransform = `translate(${-VB_SIZE / 2}, ${-VB_SIZE / 2})`

          // Сначала ставим группу в точку планеты, потом сдвигаем саму иконку.
          // Так scale можно применять между этими transform и масштабировать символ от центра.
          g.dataset.centerTransform = centerTransform
          g.dataset.iconOffsetTransform = iconOffsetTransform
          g.setAttribute('transform', `${centerTransform} ${iconOffsetTransform}`)

          const parser = new DOMParser()
          const doc = parser.parseFromString(PLANET_RAW_SVG_MAP[name], 'image/svg+xml')
          const svgEl = doc.documentElement

          // Переносим всех детей <svg> внутрь группы
          Array.from(svgEl.children).forEach(child => {
            g.appendChild(child.cloneNode(true))
          })

          return g
        }

        return null as unknown as Element
      },
    })

    const cusps = props.chart.cusps.length === 0
      ? [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330]
      : props.chart.cusps

    const planetsWithAxes = hasCusps
      ? {
        ...props.chart.planets,
        Asc: [cusps[0]],
        Mc: [cusps[9]],
      }
      : props.chart.planets

    const centerBackground = chart.paper.circle(
      chart.cx,
      chart.cy,
      chart.radius / chart.settings.INDOOR_CIRCLE_RADIUS_RATIO,
    )
    centerBackground.setAttribute('fill', chart.settings.COLOR_BACKGROUND)
    centerBackground.setAttribute('stroke', 'none')
    chart.paper.root.appendChild(centerBackground)

    const radix = chart.radix({
      planets: planetsWithAxes,
      cusps,
    })

    const aspects = props.chart.aspects.flatMap((aspect) => {
      const point = props.chart.planets[aspect.planet1]
      const toPoint = props.chart.planets[aspect.planet2]
      const name = aspectNames[aspect.aspect]

      if (!point || !toPoint || !name) return []

      return [{
        point: {
          name: aspect.planet1,
          position: point[0],
        },
        toPoint: {
          name: aspect.planet2,
          position: toPoint[0],
        },
        aspect: {
          name,
          degree: aspect.aspect,
          color: black,
          orbit: 0,
        },
        precision: aspect.orb.toFixed(4),
      }]
    })

    radix.aspects(aspects)

    const chartGroups = [
      [`${chartId}-astrology-aspects`, styles.aspects],
      [`${chartId}-astrology-radix-axis`, styles.axis],
      [`${chartId}-astrology-radix-ruler`, styles.ruler],
      [`${chartId}-astrology-radix-cusps`, styles.cusps],
      [`${chartId}-astrology-radix-circles`, styles.circles],
      [`${chartId}-astrology-radix-planets`, styles.planets],
      [`${chartId}-astrology-radix-signs`, styles.signs],
    ] as const

    chartGroups.forEach(([id, className]) => {
      document.getElementById(id)?.classList.add(className)
    })

    // const paper = document.getElementById('paper')
    // if (paper) {
    //   const aspectsGroup = document.getElementById('paper-astrology-aspects')
    //   if (aspectsGroup) {
    //     aspectsGroup.querySelectorAll('line').forEach(line => {
    //       line.setAttribute('stroke-dasharray', '6 4')
    //     })
    //   }
    // }

    return () => {
      const container = document.getElementById(chartId)
      if (container) {
        container.innerHTML = ''
      }
    }
  }, [chartId, hasCusps, props.chart, props.rotationDegrees, signSectionsColor])

  useEffect(() => {
    const container = document.getElementById(chartId)

    if (!container) return

    const aspect = props.aspect
    const accents = [
      ...(props.accents ?? []),
      ...(aspect ? [aspect[0], aspect[1]] : []),
    ]
    const accentSet = new Set<NatalChartPlanet>(accents)
    const requestedHouses = props.houses ?? []
    const shouldHighlight = accents.length > 0 || requestedHouses.length > 0
    const shouldHighlightAccentedAspects = !aspect && accentSet.size >= 2
    const aspects = container.querySelector<SVGElement>(`.${styles.aspects}`)
    const cusps = props.chart.cusps.length === 0
      ? [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330]
      : props.chart.cusps
    const activeSigns = new Set<NatalChartSign>()

    aspects?.classList.toggle(
      styles.hidden,
      shouldHighlight && !aspect && !shouldHighlightAccentedAspects,
    )

    aspects?.querySelectorAll('line').forEach((line) => {
      applyAspectLineStyle(
        line,
        props.positiveAspectColor,
        props.negativeAspectColor,
        props.aspectStrokeWidth,
      )

      if (!aspect && !shouldHighlightAccentedAspects) {
        line.classList.remove(styles.active)
        line.classList.remove(styles.hidden)
        return
      }

      const linePoint = line.getAttribute('data-point')
      const lineToPoint = line.getAttribute('data-toPoint')
      const lineDegree = line.getAttribute('data-degree')
      const active = aspect
        ? (() => {
          const [point, toPoint, degree] = aspect
          const sameDirection = linePoint === point && lineToPoint === toPoint
          const reverseDirection = linePoint === toPoint && lineToPoint === point
          const sameDegree = lineDegree === String(degree)

          return sameDegree && (sameDirection || reverseDirection)
        })()
        : (
          linePoint !== null
          && lineToPoint !== null
          && isNatalChartPlanet(linePoint)
          && isNatalChartPlanet(lineToPoint)
          && accentSet.has(linePoint)
          && accentSet.has(lineToPoint)
        )

      line.classList.toggle(styles.active, active)
      line.classList.toggle(styles.hidden, !active)
    })

    planetNames.forEach((name) => {
      const planet = container.querySelector<SVGGElement>(
        `#${CSS.escape(`${chartId}-astrology-radix-planets-${name}`)}`,
      )
      const active = accentSet.has(name)

      planet?.classList.toggle(
        styles.hidden,
        shouldHighlight && !active,
      )

      if (planet) {
        const baseTransform = planet.dataset.baseTransform
          ?? planet.getAttribute('transform')
          ?? ''
        const centerTransform = planet.dataset.centerTransform
        const iconOffsetTransform = planet.dataset.iconOffsetTransform

        planet.dataset.baseTransform = baseTransform

        if (
          shouldHighlight
          && active
          && props.accentScale
          && props.accentScale !== 1
          && centerTransform
          && iconOffsetTransform
        ) {
          planet.setAttribute(
            'transform',
            `${centerTransform} scale(${props.accentScale}) ${iconOffsetTransform}`,
          )
        }
        else {
          planet.setAttribute('transform', baseTransform)
        }
      }
    })

    accents.forEach((name) => {
      const degree = getPlanetDegree(name, props.chart, cusps, hasCusps)

      if (typeof degree !== 'number') return

      const normalizedDegree = ((degree % 360) + 360) % 360
      const sign = signNameList[Math.floor(normalizedDegree / 30)]

      activeSigns.add(sign)
    })

    signNameList.forEach((name) => {
      const sign = container.querySelector(
        `#${CSS.escape(`${chartId}-astrology-radix-signs-${name}`)}`,
      )
      const active = shouldHighlight && activeSigns.has(name)

      sign?.classList.toggle(styles.active, active)
      sign?.classList.toggle(styles.hidden, shouldHighlight && !active)
    })

    const activeHouses = new Set(requestedHouses)

    accents.forEach((name) => {
      const degree = getPlanetDegree(name, props.chart, cusps, hasCusps)

      if (typeof degree !== 'number') return

      const house = getHouseNumber(degree, cusps)

      if (house) activeHouses.add(house)
    })

    const shouldHighlightHouses = activeHouses.size > 0

    houseNumbers.forEach((house) => {
      const houseNumber = container.querySelector(
        `#${CSS.escape(`${chartId}-astrology-radix-house-${house}`)}`,
      )
      const active = shouldHighlightHouses && activeHouses.has(house)

      houseNumber?.classList.toggle(styles.active, active)
      houseNumber?.classList.toggle(styles.hidden, shouldHighlightHouses && !active)
    })
  }, [
    chartId,
    hasCusps,
    props.accents,
    props.aspect,
    props.aspectStrokeWidth,
    props.accentScale,
    props.chart,
    props.houses,
    props.negativeAspectColor,
    props.positiveAspectColor,
  ])

  return (
    <div
      id={chartId}
      class={styles.paper}
    />
  )
}
